import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import BaseUrl, { BaseUrlFE } from "../BaseUrl";
import axios from "axios";
import swal from "sweetalert";
import moment from "moment";
import 'react-quill/dist/quill.snow.css';

// BlogDetailsList.jsx
export default function BlogDetailsList() {
  const { title } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Slugify function to match titles
  const slugify = (text) => {
    return text
      .toString()
      .toLowerCase()
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/[^\w\-]+/g, '') // Remove special characters
      .replace(/\-\-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-+/, '') // Trim leading hyphens
      .replace(/-+$/, ''); // Trim trailing hyphens
  };

  const getBlogByTitle = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all blogs to find the matching title
      const blogsUrl = BaseUrl() + "api/user/getblogs";
      const blogsRes = await axios.get(blogsUrl);
      const blogs = blogsRes.data.blogList;

      // Find blog with matching title slug
      const matchedBlog = blogs.find(
        (b) => slugify(b.title) === title
      );

      if (!matchedBlog) {
        throw new Error("Blog not found");
      }

      // Fetch blog details using the matched id
      const url = BaseUrl() + `api/user/blogdetails?id=${matchedBlog.id}`;
      const res = await axios.get(url);
      const raw = res.data.blogList;
      const row = Array.isArray(raw) ? raw[0] : raw;
      setBlog(row || null);
    } catch (error) {
      setError(error.message);
      swal("Unable to load this article. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBlogByTitle();
  }, [title]);

  if (loading) {
    return <div className="text-center py-10">Loading...</div>;
  }

  if (error) {
    return <div className="text-center py-10 text-red-500">{error}</div>;
  }

  if (!blog) {
    return <div className="text-center py-10">Blog not found</div>;
  }

  const joinUrl = (base, path) => {
    if (!path) return "";
    const b = String(base).replace(/\/$/, "");
    const p = String(path).replace(/^\//, "");
    return `${b}/${p}`;
  };

  const excerptShort =
    blog?.excerpt &&
    String(blog.excerpt).trim() &&
    String(blog.excerpt).trim() !== "undefined"
      ? String(blog.excerpt).trim()
      : null;

  const imgSrc = joinUrl(BaseUrlFE, blog?.image);

  return (
    <div className="mx-auto max-w-[1440px] px-4 pb-8 pt-2 sm:px-6 lg:px-10">
      <article className="mx-auto w-full max-w-7xl rounded-2xl bg-white px-5 py-8 sm:px-10 sm:py-10">
        <p className="text-center text-sm text-gray-500">
          <span className="font-semibold text-blue-600">
            {blog?.author || "Admin"}
          </span>{" "}
          {blog?.createdAt
            ? `on ${moment(blog.createdAt).format("dddd D, MMMM YYYY")}`
            : null}
        </p>

        <h1 className="mt-2 text-center text-3xl font-bold leading-tight text-gray-900 sm:text-4xl">
          {blog?.title}
        </h1>

        {excerptShort ? (
          <p className="mx-auto mt-4 max-w-4xl text-center text-gray-600">
            {excerptShort}
          </p>
        ) : null}

        <div className="mt-4 flex justify-center gap-2">
          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800">
            {blog?.category?.name || "General"}
          </span>
        </div>

        {imgSrc ? (
          <div className="mx-auto mt-8 max-h-[min(70vh,520px)] w-full overflow-hidden rounded-xl bg-slate-100">
            <img
              src={imgSrc}
              alt={blog?.alt_tag || blog?.title || ""}
              className="h-full max-h-[min(70vh,520px)] w-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        ) : null}

        {blog?.content ? (
          <div
            className="ql-editor mt-8 space-y-4 text-lg leading-relaxed text-gray-700"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
        ) : null}
      </article>
    </div>
  );
}